import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Settings2 } from 'lucide-react';
import adminService from '../../services/admin/admin.service';
import type { Template, TemplateField, FieldType } from '../../types/admin/admin.types';
import Table, { type TableColumn } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import DynamicFieldInput from './DynamicFieldInput';
import { PageHeader, Card, SearchBox, Modal, Field, StatusPill, inputClass, usePagedList } from './ui/AdminUI';

const FIELD_TYPES: FieldType[] = ['text', 'number', 'date', 'dropdown', 'boolean', 'reference'];

// ── Page ─────────────────────────────────────────────────────────────────────
const TemplateBuilderPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Template | null>(null);
  const [tplModal, setTplModal] = useState<{ open: boolean; mode: 'create' | 'edit' }>({ open: false, mode: 'create' });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates((await adminService.listTemplates()).data);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTemplate = useCallback(async (id: number) => {
    setSelected((await adminService.getTemplate(id)).data);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const { query, setQuery, pageItems, pagination, setPage } = usePagedList<Template>(
    templates,
    (t, q) => t.name.toLowerCase().includes(q) || t.template_key.toLowerCase().includes(q),
    10
  );

  const columns: TableColumn<Template>[] = [
    { key: 'name', header: 'Name', render: (t) => <span className="font-medium text-[var(--color-gray-800)]">{t.name}</span> },
    { key: 'key', header: 'Key', render: (t) => <span className="text-mono text-xs text-[var(--color-gray-500)]">{t.template_key}</span> },
    { key: 'parent', header: 'Parent', render: (t) => t.parentTemplate ? t.parentTemplate.name : <span className="text-[var(--color-gray-400)]">— root —</span> },
    { key: 'type', header: 'Type', render: (t) => <Badge color={t.is_hierarchy_node ? 'info' : 'accent'} variant="soft">{t.is_hierarchy_node ? 'Hierarchy' : 'Entity'}</Badge> },
    { key: 'status', header: 'Status', align: 'center', render: (t) => <StatusPill active={t.is_active} /> },
    {
      key: 'actions', header: '', align: 'right', width: 130,
      render: (t) => (
        <Button size="sm" variant={selected?.id === t.id ? 'primary' : 'secondary'} onClick={(e) => { e.stopPropagation(); loadTemplate(t.id); }}>
          <Settings2 size={14} className="mr-1" /> Manage
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Template Builder"
        subtitle="Define entity & hierarchy types and their fields"
        actions={<Button size="sm" onClick={() => setTplModal({ open: true, mode: 'create' })}><Plus size={16} className="mr-1" /> New Template</Button>}
      />

      <div className="mb-4 max-w-sm">
        <SearchBox value={query} onChange={setQuery} placeholder="Search templates by name or key…" />
      </div>

      {loading ? (
        <div className="p-10 flex justify-center"><Spinner size={28} /></div>
      ) : (
        <Table
          columns={columns}
          rows={pageItems}
          rowKey={(t) => String(t.id)}
          minWidth={720}
          onRowClick={(t) => loadTemplate(t.id)}
          pagination={pagination}
          onPageChange={setPage}
          emptyState={<EmptyState title="No templates yet" description="Create your first template to get started." />}
        />
      )}

      {selected && (
        <div className="mt-6">
          <FieldsPanel
            template={selected}
            allTemplates={templates}
            onEditTemplate={() => setTplModal({ open: true, mode: 'edit' })}
            onChanged={() => loadTemplate(selected.id)}
            onStatusChanged={async () => { await loadTemplates(); await loadTemplate(selected.id); }}
            onClose={() => setSelected(null)}
          />
        </div>
      )}

      <TemplateFormModal
        open={tplModal.open}
        mode={tplModal.mode}
        template={tplModal.mode === 'edit' ? selected : null}
        allTemplates={templates}
        onClose={() => setTplModal({ ...tplModal, open: false })}
        onSaved={async (id) => {
          setTplModal({ ...tplModal, open: false });
          await loadTemplates();
          if (id) await loadTemplate(id);
        }}
      />
    </div>
  );
};

// ── Template create/edit modal ───────────────────────────────────────────────
const TemplateFormModal: React.FC<{
  open: boolean; mode: 'create' | 'edit'; template: Template | null; allTemplates: Template[];
  onClose: () => void; onSaved: (id?: number) => void;
}> = ({ open, mode, template, allTemplates, onClose, onSaved }) => {
  const [form, setForm] = useState({ template_key: '', name: '', description: '', parent_template_id: '', is_hierarchy_node: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && template) {
      setForm({
        template_key: template.template_key,
        name: template.name,
        description: template.description || '',
        parent_template_id: template.parent_template_id ? String(template.parent_template_id) : '',
        is_hierarchy_node: template.is_hierarchy_node,
      });
    } else {
      setForm({ template_key: '', name: '', description: '', parent_template_id: '', is_hierarchy_node: true });
    }
  }, [open, mode, template]);

  const submit = async () => {
    if (!form.name || (mode === 'create' && !form.template_key)) return toast.error('Name and key are required.');
    setSaving(true);
    try {
      const body: any = {
        name: form.name,
        description: form.description,
        parent_template_id: form.parent_template_id ? Number(form.parent_template_id) : null,
        is_hierarchy_node: form.is_hierarchy_node,
      };
      let id: number | undefined;
      if (mode === 'create') {
        body.template_key = form.template_key;
        id = (await adminService.createTemplate(body)).data.id;
      } else if (template) {
        id = template.id;
        await adminService.updateTemplate(template.id, body);
      }
      toast.success(`Template ${mode === 'create' ? 'created' : 'updated'}.`);
      onSaved(id);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={mode === 'create' ? 'New Template' : 'Edit Template'}
      onClose={onClose}
      footer={<><Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button><Button size="sm" onClick={submit} disabled={saving}>{saving ? <Spinner size={14} /> : 'Save'}</Button></>}
    >
      <div className="space-y-4">
        <Field label="Template key" required hint={mode === 'edit' ? 'Immutable — cannot be changed.' : 'lower_snake_case, e.g. police_station'}>
          <input className={inputClass} disabled={mode === 'edit'} value={form.template_key} onChange={(e) => setForm({ ...form, template_key: e.target.value })} />
        </Field>
        <Field label="Display name" required>
          <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Description">
          <input className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Parent template" hint="Instances must hang off a parent of this type. Changeable only while there are no instances.">
          <select className={inputClass} value={form.parent_template_id} onChange={(e) => setForm({ ...form, parent_template_id: e.target.value })}>
            <option value="">— none (root) —</option>
            {allTemplates.filter((t) => t.id !== template?.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="!min-h-0 w-4 h-4" checked={form.is_hierarchy_node} onChange={(e) => setForm({ ...form, is_hierarchy_node: e.target.checked })} />
          Participates in the org tree (hierarchy node)
        </label>
      </div>
    </Modal>
  );
};

// ── Fields panel (detail) ────────────────────────────────────────────────────
const FieldsPanel: React.FC<{
  template: Template; allTemplates: Template[]; onEditTemplate: () => void;
  onChanged: () => void; onStatusChanged: () => void; onClose: () => void;
}> = ({ template, allTemplates, onEditTemplate, onChanged, onStatusChanged, onClose }) => {
  const fields = useMemo(() => (template.fields || []).slice().sort((a, b) => a.display_order - b.display_order), [template.fields]);
  const [preview, setPreview] = useState<Record<string, unknown>>({});
  const [fieldModal, setFieldModal] = useState<{ open: boolean; field: TemplateField | null }>({ open: false, field: null });
  const [instanceCount, setInstanceCount] = useState(0);

  useEffect(() => {
    adminService.listNodes({ template_id: template.id, limit: 1 }).then((r) => setInstanceCount(r.pagination?.total ?? 0)).catch(() => setInstanceCount(0));
  }, [template.id]);

  const move = async (index: number, dir: -1 | 1) => {
    const order = fields.map((f) => f.id);
    const j = index + dir;
    if (j < 0 || j >= order.length) return;
    [order[index], order[j]] = [order[j], order[index]];
    try { await adminService.reorderFields(template.id, order); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Reorder failed.'); }
  };

  const toggleField = async (f: TemplateField) => {
    try { await adminService.setFieldStatus(template.id, f.id, !f.is_active); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed.'); }
  };

  const removeField = async (f: TemplateField) => {
    if (!window.confirm(`Delete field "${f.label}"? Only allowed if no instances exist.`)) return;
    try { await adminService.deleteField(template.id, f.id); toast.success('Field deleted.'); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Delete failed — deactivate instead.'); }
  };

  const toggleTemplate = async () => {
    try { await adminService.setTemplateStatus(template.id, !template.is_active, true); onStatusChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed.'); }
  };

  const ruleChips = (f: TemplateField) => {
    const r = f.validation_rules || {};
    const chips: string[] = [];
    if (r.required) chips.push('required');
    if (r.unique) chips.push('unique');
    if (f.field_type === 'dropdown' && r.options) chips.push(`${r.options.length} options`);
    if (f.field_type === 'reference') chips.push(r.multiple ? 'multi-ref' : 'ref');
    return chips;
  };

  const columns: TableColumn<TemplateField>[] = [
    { key: 'order', header: '#', width: 70, render: (f, i) => (
      <div className="flex items-center gap-0.5">
        <button className="text-[var(--color-gray-400)] hover:text-[var(--color-gray-700)] disabled:opacity-30" disabled={i === 0} onClick={(e) => { e.stopPropagation(); move(i, -1); }}><ArrowUp size={14} /></button>
        <button className="text-[var(--color-gray-400)] hover:text-[var(--color-gray-700)] disabled:opacity-30" disabled={i === fields.length - 1} onClick={(e) => { e.stopPropagation(); move(i, 1); }}><ArrowDown size={14} /></button>
      </div>
    ) },
    { key: 'label', header: 'Label', render: (f) => <span className="font-medium text-[var(--color-gray-800)]">{f.label}</span> },
    { key: 'key', header: 'Key', render: (f) => <span className="text-mono text-xs text-[var(--color-gray-500)]">{f.field_key}</span> },
    { key: 'type', header: 'Type', render: (f) => <Badge color="neutral" variant="soft">{f.field_type}</Badge> },
    { key: 'rules', header: 'Rules', render: (f) => (
      <div className="flex flex-wrap gap-1">{ruleChips(f).map((c) => <Badge key={c} color="info" variant="soft">{c}</Badge>)}</div>
    ) },
    { key: 'status', header: 'Status', align: 'center', render: (f) => <StatusPill active={f.is_active} /> },
    { key: 'actions', header: '', align: 'right', width: 150, render: (f) => (
      <div className="flex items-center justify-end gap-1">
        <button title="Edit" className="p-1.5 rounded hover:bg-[var(--color-gray-100)] text-[var(--color-gray-500)]" onClick={(e) => { e.stopPropagation(); setFieldModal({ open: true, field: f }); }}><Pencil size={15} /></button>
        <button title={f.is_active ? 'Deactivate' : 'Activate'} className="px-2 py-1 rounded hover:bg-[var(--color-gray-100)] text-xs text-[var(--color-gray-600)]" onClick={(e) => { e.stopPropagation(); toggleField(f); }}>{f.is_active ? 'Off' : 'On'}</button>
        <button title="Delete" className="p-1.5 rounded hover:bg-[var(--color-error-light)] text-[var(--color-error)]" onClick={(e) => { e.stopPropagation(); removeField(f); }}><Trash2 size={15} /></button>
      </div>
    ) },
  ];

  return (
    <Card>
      <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-gray-100)]">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[var(--font-size-lg)] font-semibold text-[var(--color-gray-900)]">{template.name}</h2>
            <StatusPill active={template.is_active} />
          </div>
          <p className="text-xs text-[var(--color-gray-500)] mt-0.5">
            <span className="text-mono">{template.template_key}</span>
            {template.parentTemplate ? ` · child of ${template.parentTemplate.name}` : ' · root'}
            {` · ${instanceCount} instance${instanceCount === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={onEditTemplate}><Pencil size={14} className="mr-1" /> Edit</Button>
          <Button size="sm" variant="secondary" onClick={toggleTemplate}>{template.is_active ? 'Deactivate' : 'Activate'}</Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--color-gray-600)] uppercase tracking-wide">Fields</h3>
          <Button size="sm" onClick={() => setFieldModal({ open: true, field: null })}><Plus size={14} className="mr-1" /> Add Field</Button>
        </div>
        <Table
          columns={columns}
          rows={fields}
          rowKey={(f) => String(f.id)}
          minWidth={720}
          emptyState={<EmptyState title="No fields yet" description="Add fields to define this template's form." />}
        />

        {fields.some((f) => f.is_active) && (
          <div className="mt-5">
            <h3 className="text-sm font-semibold text-[var(--color-gray-600)] uppercase tracking-wide mb-3">Form preview</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              {fields.filter((f) => f.is_active).map((f) => (
                <DynamicFieldInput key={f.id} field={f} value={preview[f.field_key]} onChange={(v) => setPreview({ ...preview, [f.field_key]: v })} disabled />
              ))}
            </div>
          </div>
        )}
      </div>

      <FieldFormModal
        open={fieldModal.open}
        field={fieldModal.field}
        templateId={template.id}
        allTemplates={allTemplates}
        hasInstances={instanceCount > 0}
        onClose={() => setFieldModal({ open: false, field: null })}
        onSaved={() => { setFieldModal({ open: false, field: null }); onChanged(); }}
      />
    </Card>
  );
};

// ── Field add/edit modal ─────────────────────────────────────────────────────
const FieldFormModal: React.FC<{
  open: boolean; field: TemplateField | null; templateId: number; allTemplates: Template[]; hasInstances: boolean;
  onClose: () => void; onSaved: () => void;
}> = ({ open, field, templateId, allTemplates, hasInstances, onClose, onSaved }) => {
  const isEdit = !!field;
  const [f, setF] = useState<any>({ field_key: '', label: '', field_type: 'text', required: false, unique: false, options: '', referenced_template_id: '', multiple: false, min: '', max: '', regex: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (field) {
      const r = field.validation_rules || {};
      setF({
        field_key: field.field_key, label: field.label, field_type: field.field_type,
        required: !!r.required, unique: !!r.unique, options: (r.options || []).join(', '),
        referenced_template_id: r.referenced_template_id ? String(r.referenced_template_id) : '', multiple: !!r.multiple,
        min: r.min ?? '', max: r.max ?? '', regex: r.regex ?? '',
      });
    } else {
      setF({ field_key: '', label: '', field_type: 'text', required: false, unique: false, options: '', referenced_template_id: '', multiple: false, min: '', max: '', regex: '' });
    }
  }, [open, field]);

  const buildRules = () => {
    const rules: any = {};
    if (f.required) rules.required = true;
    if (f.unique) rules.unique = true;
    if (f.field_type === 'dropdown') rules.options = String(f.options).split(',').map((s: string) => s.trim()).filter(Boolean);
    if (f.field_type === 'reference') { rules.referenced_template_id = Number(f.referenced_template_id); rules.multiple = !!f.multiple; }
    if ((f.field_type === 'number' || f.field_type === 'text') && f.min !== '') rules.min = Number(f.min);
    if ((f.field_type === 'number' || f.field_type === 'text') && f.max !== '') rules.max = Number(f.max);
    if (f.field_type === 'text' && f.regex) rules.regex = f.regex;
    return rules;
  };

  const submit = async () => {
    if (!f.field_key || !f.label) return toast.error('Key and label are required.');
    if (f.field_type === 'reference' && !f.referenced_template_id) return toast.error('Pick a referenced template.');
    setSaving(true);
    try {
      const rules = buildRules();
      if (isEdit && field) {
        await adminService.updateField(templateId, field.id, { label: f.label, field_type: f.field_type, validation_rules: rules } as any);
      } else {
        await adminService.createField(templateId, { field_key: f.field_key, label: f.label, field_type: f.field_type, validation_rules: rules } as any);
      }
      toast.success(`Field ${isEdit ? 'updated' : 'added'}.`);
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save field.');
    } finally {
      setSaving(false);
    }
  };

  const lockType = isEdit && hasInstances;

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Field' : 'Add Field'}
      onClose={onClose}
      footer={<><Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button><Button size="sm" onClick={submit} disabled={saving}>{saving ? <Spinner size={14} /> : 'Save'}</Button></>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Field key" required hint={isEdit ? 'Immutable once instances exist.' : 'lower_snake_case'}>
            <input className={inputClass} disabled={isEdit} value={f.field_key} onChange={(e) => setF({ ...f, field_key: e.target.value })} />
          </Field>
          <Field label="Label" required>
            <input className={inputClass} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          </Field>
        </div>
        <Field label="Type" hint={lockType ? 'Type is locked because instances exist.' : undefined}>
          <select className={inputClass} disabled={lockType} value={f.field_type} onChange={(e) => setF({ ...f, field_type: e.target.value })}>
            {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="!min-h-0 w-4 h-4" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} /> Required</label>
          {f.field_type !== 'boolean' && f.field_type !== 'reference' && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="!min-h-0 w-4 h-4" checked={f.unique} onChange={(e) => setF({ ...f, unique: e.target.checked })} /> Unique</label>
          )}
          {f.field_type === 'reference' && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="!min-h-0 w-4 h-4" checked={f.multiple} onChange={(e) => setF({ ...f, multiple: e.target.checked })} /> Allow multiple</label>
          )}
        </div>

        {f.field_type === 'dropdown' && (
          <Field label="Options" hint="Comma-separated"><input className={inputClass} value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} /></Field>
        )}
        {f.field_type === 'reference' && (
          <Field label="Referenced template" required>
            <select className={inputClass} value={f.referenced_template_id} onChange={(e) => setF({ ...f, referenced_template_id: e.target.value })}>
              <option value="">— select —</option>
              {allTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
        )}
        {(f.field_type === 'number' || f.field_type === 'text') && (
          <div className="grid grid-cols-2 gap-3">
            <Field label={f.field_type === 'text' ? 'Min length' : 'Min value'}><input className={inputClass} value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })} /></Field>
            <Field label={f.field_type === 'text' ? 'Max length' : 'Max value'}><input className={inputClass} value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })} /></Field>
          </div>
        )}
        {f.field_type === 'text' && (
          <Field label="Regex pattern" hint="Optional"><input className={inputClass} value={f.regex} onChange={(e) => setF({ ...f, regex: e.target.value })} /></Field>
        )}
      </div>
    </Modal>
  );
};

export default TemplateBuilderPage;
